import Flickity from "flickity";
import 'flickity-fade';

toggleResizedClass(Flickity);

export function sliderName(element, index) {
    return (element.dataset.slider ? element.dataset.slider : `slider_${index + 1}`);
}

/**
 * @param {Flickity} Flickity
 */
export function toggleResizedClass(slider) {
    let resize = slider.prototype.resize;

    slider.prototype.resize = function () {
        this.element.classList.remove('flickity-resized');
        resize.call(this);
        this.element.classList.add('flickity-resized');
    };
}

/**
 * @param {Flickity} slider
 */
export function wrapControls(slider) {
    if (slider.prevButton) {
        slider.prevButton.element.innerHTML = LandedHouses.svgs.chevronLeft;
    }

    if (slider.nextButton) {
        slider.nextButton.element.innerHTML = LandedHouses.svgs.chevronRight;
    }

    // Vanilla replacement for jQuery's wrapAll – slider.$element is Flickity's
    // optional jQuery bridge and no longer exists now the theme doesn't load
    // jQuery. Without the .flickity__controls wrapper the arrows lose the CSS
    // that spreads them to either edge and pile up next to the progress bar,
    // and the throw here also killed the rest of the ready handler.
    if (slider.prevButton || slider.nextButton) {
        const buttons = slider.element.querySelectorAll(':scope > .flickity-button');

        let controls = slider.element.querySelector('.flickity__controls');

        if (buttons.length && !controls) {
            controls = document.createElement('div');
            controls.className = 'flickity__controls';
            buttons[0].before(controls);
            buttons.forEach((button) => controls.append(button));
        }

        // Header slot: a carousel that names a slot (data-controls-slot="<id>")
        // gets its arrow pair moved into that element - the section's heading
        // row, so the title and the arrows share a line and the arrows never
        // sit over a card. The buttons are the same DOM nodes Flickity bound,
        // so enable/disable keeps working from outside the container. No slot,
        // or a missing element, and the controls stay where they are - a
        // template that forgets its slot degrades to arrows above the track,
        // not to an error.
        const slotId = slider.element.dataset.controlsSlot;

        if (controls && slotId) {
            const slot = document.getElementById(slotId);

            if (slot) {
                slot.append(controls);
            }
        }
    }
}

/**
 * Flickity marks every non-selected cell aria-hidden="true". On a carousel that
 * shows several cells at once that hides cards which are plainly on screen, and
 * the links inside them stay focusable – which is exactly what Lighthouse
 * reports as "[aria-hidden] elements contain focusable descendants". Recompute
 * the state from what is actually in the viewport, and pair aria-hidden with
 * `inert` so a genuinely off-screen cell can't be tabbed into either.
 *
 * @param {Flickity} slider
 */
export function syncCellVisibility(slider) {
    const viewport = slider.viewport.getBoundingClientRect();

    // Every measurement first, every attribute write second. Interleaving them
    // is layout thrashing: each setAttribute invalidates layout, so the next
    // getBoundingClientRect has to force a synchronous reflow - once per cell,
    // on a carousel that can hold twenty of them.
    const inView = slider.cells.map((cell, index) => {
        // Fade sliders stack every cell in the same place, so geometry can't
        // tell them apart – only the selected one is visible.
        if (slider.options.fade) {
            return index === slider.selectedIndex;
        }

        const bounds = cell.element.getBoundingClientRect();

        // 1px of slack so a sub-pixel overlap doesn't count as in view.
        return bounds.right > viewport.left + 1 && bounds.left < viewport.right - 1;
    });

    slider.cells.forEach((cell, index) => {
        if (inView[index]) {
            cell.element.removeAttribute('aria-hidden');
            cell.element.removeAttribute('inert');
        } else {
            cell.element.setAttribute('aria-hidden', 'true');
            cell.element.setAttribute('inert', '');
        }
    });
}

/**
 * While a sliding carousel is mid-flight the cell positions are still moving,
 * so nothing can be marked reliably – expose everything until it settles.
 *
 * @param {Flickity} slider
 */
export function exposeAllCells(slider) {
    slider.cells.forEach((cell) => {
        cell.element.removeAttribute('aria-hidden');
        cell.element.removeAttribute('inert');
    });
}

/**
 * @param {Flickity} slider
 */
function getSlidesInView(slider) {
    let amount = Math.round(slider.size.width / slider.selectedSlide.outerWidth);

    if (LandedHouses.window.width <= 450) {
        amount = amount - 1;
    }

    return amount;
}

/**
 * @param {Flickity} slider
 */
export function addProgressBar(slider) {
    if (!slider.options.progressBar || slider.slides.length <= getSlidesInView(slider)) {
        return;
    }

    let element = document.createElement('div');
    element.classList.add('flickity-progress');

    let innerElement = document.createElement('div');
    innerElement.classList.add('flickity-progress__inner');

    element.append(innerElement);

    let slideCount = slider.slides.length;

    innerElement.style.width = `${(100 / slideCount)}%`;

    slider.viewport.after(element);

    updateProgressBar(slider);
}

/**
 * @param {Flickity} slider
 */
export function updateProgressBar(slider) {
    let innerElement = slider.element.querySelector('.flickity-progress__inner');

    if (!slider.options.progressBar || !innerElement) {
        return;
    }

    let slideCount = slider.slides.length;
    let currentSlide = slider.selectedIndex + 1;

    let slidesInView = getSlidesInView(slider);

    if (slidesInView > 1) {
        slideCount = slideCount - (slidesInView - 1);
    }

    innerElement.style.width = `${(currentSlide / slideCount) * 100}%`;
}

function disableContainedSlides(slider) {
    if (slider.options.wrapAround) {
        return;
    }

    let slidesInView = getSlidesInView(slider);

    if (slider.selectedIndex >= slider.cells.length - slidesInView) {
        if (slider.nextButton) {
            slider.nextButton.element.setAttribute('disabled', '');
        }

        if (slider.selectedIndex > slider.cells.length - slidesInView) {
            slider.select(slider.cells.length - slidesInView);
        }
    } else {
        if (slider.nextButton) {
            slider.nextButton.element.removeAttribute('disabled');
        }
    }
}

function controlsSlider(slider) {
    if (!slider.options.controlsSlider) {
        return;
    }

    let otherSlider = window.LandedHouses.sliders[slider.options.controlsSlider];

    if (!otherSlider) {
        return;
    }

    let currentIndex = slider.selectedIndex;

    otherSlider.select(currentIndex);
}

function pageDotsAsImages(slider) {
    if (!slider.options.pageDotsAsImages) {
        return;
    }

    for (let i = 0; i < slider.cells.length; i++) {
        const cell = slider.cells[i];
        const pagerImgUrl = cell.element.getAttribute('data-page-dot-image-url');

        if (!pagerImgUrl) {
            continue;
        }

        const pageDot = slider.pageDots.dots[i];
        pageDot.setAttribute('aria-label', `Slide ${i + 1}`);
        pageDot.innerHTML = `<img width="92" height="52" class="w-full" src="${pagerImgUrl}" alt="Slide ${i + 1}">`;
    }

    for (let i = 0; i < slider.pageDots.dots.length; i++) {
        const pageDot = slider.pageDots.dots[i];

        pageDot.addEventListener('click', function () {
            setTimeout(() => {
                slider.stopPlayer();
                slider.playPlayer();
            }, 100);
        });
    }
}

function updateCounter(slider) {
    const name = slider.element.dataset.slider;
    const element = document.querySelector(`[data-flickity-count="${name}"]`);

    if (!element) {
        return;
    }

    element.innerHTML = slider.selectedIndex + 1;
}

function hidePageDots(slider) {
    if (!slider.pageDots) {
        return;
    }

    if (slider.pageDots.dots.length <= 1) {
        slider.pageDots.holder.style.display = 'none';
    } else {
        slider.pageDots.holder.style.display = '';
    }
}

export function flickitySettings(slider) {
    let settings = JSON.parse(slider.dataset.settings ?? '{}'),
        type = slider.dataset.slider ?? '';

    switch (type) {
        default:
            return {
                ...{
                    adaptiveHeight: true,
                    lazyLoad: true,
                    pageDots: false,
                    cellAlign: 'left',
                    on: {
                        ready: function () {
                            this.element.classList.add('flickity-resized');

                            addProgressBar(this);
                            wrapControls(this);
                            disableContainedSlides(this);
                            pageDotsAsImages(this);
                            hidePageDots(this);
                            syncCellVisibility(this);
                        },
                        change: function () {
                            updateProgressBar(this);
                            disableContainedSlides(this);
                            controlsSlider(this);
                            updateCounter(this);
                            hidePageDots(this);

                            // A fade slider's selected index is already the
                            // whole answer; a sliding one has to wait for the
                            // cells to stop moving.
                            if (this.options.fade) {
                                syncCellVisibility(this);
                            } else {
                                exposeAllCells(this);
                            }
                        },
                        settle: function () {
                            syncCellVisibility(this);
                        },
                        resize: function () {
                            // Flickity emits this before it re-selects and
                            // repositions the slider, so measure on the next
                            // frame or the cell rects are a frame stale.
                            requestAnimationFrame(() => syncCellVisibility(this));
                        },
                        dragStart: function () {
                            this.element.classList.add('flickity-dragging');
                        },
                        dragEnd: function () {
                            setTimeout(function (slider) {
                                slider.element.classList.remove('flickity-dragging');
                            }, 100, this);

                            if (settings.autoPlay) {
                                this.stopPlayer();
                                this.playPlayer();
                            }
                        }
                    },
                },
                ...settings,
            };
    }
}

export function sliders() {
    document.querySelectorAll('[data-slider]').forEach((element, index) => {
        const name = sliderName(element, index);

        LandedHouses.sliders[name] = new Flickity(element, flickitySettings(element));

        LandedHouses.sliders[name].on('change', (index) => {
            window.dispatchEvent(new CustomEvent('landedHouses/flickity/change', {
                detail: {
                    name: name,
                    index: index,
                }
            }));
        });
    });
}

/**
 * Load sliders
 */
export default function flickitySliders() {
    window.addEventListener('landedHouses/flickity/load', () => {
        sliders();
    });

    window.dispatchEvent(new CustomEvent('landedHouses/flickity/load'));
}
